import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../services/AuthContext';
import { RoomSelector } from './RoomSelector';
import { EventosHubLogo } from './EventosHubLogo';
import { nomineeAPI, roomAPI } from '../services/api';
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const Title = styled.h1`
  fontSize: 3rem;
  fontWeight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  fontSize: 1.2rem;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const WelcomeMessage = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  h2 {
    margin: 0;
    fontSize: 1.5rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const MenuCard = styled.div<{ $delay?: number }>`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => (props.$delay || 0) * 0.1}s;
  animation-fill-mode: both;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const CardIcon = styled.div`
  fontSize: 3rem;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  color: #1f2937;
  fontSize: 1.5rem;
  fontWeight: 600;
  margin-bottom: 1rem;
`;

const CardDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CardButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  fontWeight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
`;

const LogoutButton = styled.button`
  position: fixed;
  top: 2rem;
  right: 2rem;
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  fontWeight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #5a67d8;
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    position: static;
    margin: 2rem auto 0;
    display: block;
  }
`;

const RoomInfo = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  text-align: center;
  
  h3 {
    margin: 0 0 0.5rem 0;
    fontSize: 1.3rem;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    fontSize: 0.9rem;
  }
`;

const BackToRoomsButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  fontWeight: 500;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const NomineesInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  fontSize: 0.9rem;
  text-align: center;
  
  strong {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

interface MenuOption {
  icon: string;
  title: string;
  description: string;
  path: string;
  buttonText: string;
}

interface Room {
  id: number;
  name: string;
  description: string;
  code: string;
  member_count: number;
  is_admin: boolean;
  created_at: string;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [greeting, setGreeting] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [nominees, setNominees] = useState<any[]>([]);

  const roomId = searchParams.get('room_id');

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('¡Buenos días');
    } else if (hour < 18) {
      setGreeting('¡Buenas tardes');
    } else {
      setGreeting('¡Buenas noches');
    }
  }, []);

  const loadRoomData = useCallback(async () => {
    if (!roomId) return;
    try {
      const [roomData, nomineesData] = await Promise.all([
        roomAPI.getRoomDetails(parseInt(roomId)),
        nomineeAPI.getRoomNominees(parseInt(roomId))
      ]);
      setSelectedRoom(roomData);
      setNominees(nomineesData);
    } catch (error) {
      console.error('Error loading room data:', error);
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadRoomData();
    }
  }, [roomId, loadRoomData]);

  const handleRoomSelect = (room: Room) => {
    setSelectedRoom(room);
    setSearchParams({ room_id: room.id.toString() });
  };

  const handleBackToRooms = () => {
    setSelectedRoom(null);
    setSearchParams({});
  };

  const menuOptions: MenuOption[] = [
    {
      icon: '👥',
      title: 'Gestionar Nominados',
      description: 'Crea y gestiona los nominados para las votaciones de tu sala.',
      path: `/nominees?room_id=${selectedRoom?.id}`,
      buttonText: 'Gestionar Nominados'
    },
    {
      icon: '🏆',
      title: 'Gestionar Categorías',
      description: 'Crea categorías personalizadas como "Mejor Amigo", "Más Inteligente", etc.',
      path: `/categories?room_id=${selectedRoom?.id}`,
      buttonText: 'Gestionar Categorías'
    },
    {
      icon: '📸',
      title: 'Subir Foto',
      description: 'Sube la mejor foto para mostrar junto con el ganador de cada categoría.',
      path: `/upload-photo?room_id=${selectedRoom?.id}`,
      buttonText: 'Subir Foto'
    },
    {
      icon: '🏷️',
      title: 'Crear Apodos',
      description: 'Propón apodos creativos que se mostrarán junto con los ganadores.',
      path: `/nickname?room_id=${selectedRoom?.id}`,
      buttonText: 'Crear Apodos'
    },
    {
      icon: '🗳️',
      title: 'Votar por Trofeos',
      description: 'Participa votando por quien debería ganar cada categoría entre tus amigos.',
      path: `/vote?room_id=${selectedRoom?.id}`,
      buttonText: 'Ir a Votar'
    },
    {
      icon: '📊',
      title: 'Ver Resultados',
      description: 'Consulta los resultados finales con fotos, apodos y animaciones.',
      path: `/results?room_id=${selectedRoom?.id}`,
      buttonText: 'Ver Resultados'
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Si no hay sala seleccionada, mostrar selector de salas
  if (!selectedRoom || !roomId) {
    return (
      <Container>
        <LogoutButton onClick={handleLogout}>
          Cerrar Sesión
        </LogoutButton>
        
        <Header>
          <EventosHubLogo size={350} />
          
          {user && (
            <WelcomeMessage>
              <h2>{greeting}, {user.name}!</h2>
              <p>Selecciona una sala para comenzar</p>
            </WelcomeMessage>
          )}
        </Header>

        <RoomSelector onRoomSelect={handleRoomSelect} />
      </Container>
    );
  }

  // Mostrar dashboard de la sala seleccionada
  return (
    <Container>
      <LogoutButton onClick={handleLogout}>
        Cerrar Sesión
      </LogoutButton>
      
      <Header>
        <EventosHubLogo size={350} />
        
        <BackToRoomsButton onClick={handleBackToRooms}>
          ← Cambiar de Sala
        </BackToRoomsButton>
        
        {selectedRoom && (
          <RoomInfo>
            <h3>🏠 {selectedRoom.name}</h3>
            <p>{selectedRoom.description}</p>
            <p>Código: <strong>{selectedRoom.code}</strong> | {selectedRoom.member_count} miembros</p>
            {selectedRoom.is_admin && <p>👑 Eres administrador de esta sala</p>}
          </RoomInfo>
        )}
        
        {user && (
          <WelcomeMessage>
            <h2>{greeting}, {user.name}!</h2>
            <p>¿Qué te gustaría hacer en esta sala?</p>
            {nominees.length > 0 && (
              <NomineesInfo>
                <strong>📋 Nominados disponibles:</strong>
                {nominees.map(nominee => nominee.name).join(', ')}
              </NomineesInfo>
            )}
          </WelcomeMessage>
        )}
      </Header>

      <MenuGrid>
        {menuOptions.map((option, index) => (
          <MenuCard
            key={option.path}
            $delay={index}
            onClick={() => navigate(option.path)}
          >
            <CardIcon>{option.icon}</CardIcon>
            <CardTitle>{option.title}</CardTitle>
            <CardDescription>{option.description}</CardDescription>
            <CardButton>{option.buttonText}</CardButton>
          </MenuCard>
        ))}
      </MenuGrid>
    </Container>
  );
};

export default Dashboard;